import React, { useEffect, useState } from 'react';
import { Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useDeck } from './store';
import { api, errMsg } from './api';
import { conclIndex, nameOf, Team } from './slides';
import { LIBRARY, libText } from './sources';

const input = 'w-full rounded-lg border border-neutral-300 px-3 py-2 font-ui text-sm outline-none focus:border-blue-600 transition-colors';
const primary = 'rounded-full bg-black px-5 py-2.5 font-app text-sm font-semibold text-white hover:bg-blue-600 active:scale-[0.98] transition-[background-color,transform] disabled:opacity-50';

export function UnlockDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { unlock, author, team } = useDeck();
  const [code, setCode] = useState('');
  const [name, setName] = useState(author);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('Indiquez votre prénom.');
    setBusy(true);
    try { await unlock(code, name.trim()); toast.success('Édition activée sur cet ordinateur.'); onClose(); setCode(''); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="font-ui" data-testid="unlock-dialog">
        <DialogHeader><DialogTitle className="font-app">Activer la modification</DialogTitle><DialogDescription>Entrez le code de groupe. Sans lui, la présentation reste en lecture seule.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <input data-testid="unlock-name-input" list="team-names" className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Votre prénom" />
          <datalist id="team-names">{team.names.filter(Boolean).map((n) => <option key={n} value={n} />)}</datalist>
          <input data-testid="unlock-code-input" type="password" className={input} value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code de groupe" autoFocus />
          <button data-testid="unlock-submit-button" disabled={busy} className={primary}>{busy ? 'Vérification…' : 'Déverrouiller'}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function TeamDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { team, set, slug } = useDeck();
  const [t, setT] = useState<Team>(team);
  const [oldC, setOldC] = useState('');
  const [newC, setNewC] = useState('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setT(team); }, [open]);
  const save = () => {
    if (t.intro1 === t.intro2) return toast.error("Les deux parties de l'intro doivent être faites par deux élèves différents.");
    set('team', t);
    toast.success('Rôles mis à jour pour toutes les slides.');
    onClose();
  };
  const changeCode = async () => {
    try { await api.changeCode(slug, oldC, newC); toast.success('Code modifié. Communiquez-le à vos camarades.'); setOldC(''); setNewC(''); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const sel = (k: 'intro1' | 'intro2') => (
    <select data-testid={`team-${k}-select`} className={input} value={t[k]} onChange={(e) => setT({ ...t, [k]: Number(e.target.value) })}>
      {[0, 1, 2].map((i) => <option key={i} value={i}>{nameOf(i, t)}</option>)}
    </select>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg font-ui" data-testid="team-dialog">
        <DialogHeader><DialogTitle className="font-app">Le groupe</DialogTitle><DialogDescription>Réglez les prénoms une fois : « qui parle » se met à jour sur toutes les slides.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-3 gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-neutral-500">Élève {i + 1}</label>
              <input data-testid={`team-name-${i}-input`} className={input} value={t.names[i]} onChange={(e) => { const names = [...t.names]; names[i] = e.target.value; setT({ ...t, names }); }} placeholder={`Prénom ${i + 1}`} />
            </div>
          ))}
        </div>
        <p className="text-xs text-neutral-500">Élève 1 présente I.A et II.A, Élève 2 I.B et II.B, Élève 3 I.C et II.C.</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1"><label className="text-xs font-semibold text-neutral-500">Intro — partie 1 (slide 2)</label>{sel('intro1')}</div>
          <div className="flex flex-col gap-1"><label className="text-xs font-semibold text-neutral-500">Intro — partie 2 (slide 3)</label>{sel('intro2')}</div>
        </div>
        <p className="rounded-lg bg-neutral-100 px-3 py-2 text-sm" data-testid="team-conclusion-info">Conclusion (verdict) : <b>{t.intro1 === t.intro2 ? '—' : nameOf(conclIndex(t), t)}</b></p>
        <button data-testid="team-save-button" onClick={save} className={primary}>Enregistrer</button>
        <div className="mt-2 border-t pt-4">
          <p className="mb-2 text-sm font-semibold">Changer le code de groupe</p>
          <div className="flex gap-2">
            <input data-testid="change-code-old-input" type="password" className={input} value={oldC} onChange={(e) => setOldC(e.target.value)} placeholder="Code actuel" />
            <input data-testid="change-code-new-input" type="password" className={input} value={newC} onChange={(e) => setNewC(e.target.value)} placeholder="Nouveau code" />
            <button data-testid="change-code-button" onClick={changeCode} disabled={newC.length < 4 || !oldC} className={primary}>OK</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function LibrarySheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const cats = Array.from(new Set(LIBRARY.map((s) => s.cat)));
  const copy = (t: string) => { navigator.clipboard?.writeText(t); toast.success('Référence copiée.'); };
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-[520px] overflow-auto font-ui sm:max-w-[520px]" data-testid="library-sheet">
        <SheetHeader>
          <SheetTitle className="font-app">Sources sûres</SheetTitle>
          <SheetDescription>Sites officiels (juridictions, Légifrance, UE, statistique publique) et bases académiques acceptées à l'université. Ce sont des points de départ : ouvrez le lien, citez la page exacte consultée et ajoutez la date de consultation. Évitez blogs, forums et IA comme source.</SheetDescription>
        </SheetHeader>
        <div className="mt-6 flex flex-col gap-6">
          {cats.map((c) => (
            <div key={c} className="flex flex-col gap-2">
              <h3 className="font-type text-xs uppercase tracking-[0.2em] text-neutral-500">{c}</h3>
              {LIBRARY.filter((s) => s.cat === c).map((s) => (
                <div key={s.id} className="rounded-xl border border-neutral-200 p-3 text-sm" data-testid={`library-item-${s.id}`}>
                  <p>{s.citation}</p>
                  <div className="mt-2 flex gap-2">
                    <a href={s.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline"><ExternalLink size={12} /> Ouvrir</a>
                    <button onClick={() => copy(libText(s))} className="flex items-center gap-1 text-xs font-semibold text-neutral-600 hover:text-black" data-testid={`library-copy-${s.id}`}><Copy size={12} /> Copier</button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
